import { GridApi, IRowNode } from 'ag-grid-community';
import { useEffect } from 'react';
import { useLocalStorage } from 'helpers/reactUseHelper';
import {
    BUDGET_LINE_ID,
    INSCRIPTION_ID,
    SPLIT_BY,
} from 'routes/BudgetModule/config/constants';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';

const DEFAULT_ROW_PER_PAGE = 100;

// find the node that has key and value that match input condition
const findInGrid = (
    gridApi: GridApi,
    condition: (tuple: [key: string, value: unknown]) => boolean,
) => {
    let targetNode: IRowNode | undefined | null;
    gridApi.forEachNode((node) => {
        if (!node.data) return;
        const ids = Object.entries(node.data)
            .filter(([key, value]) => condition([key, value]))
            .reduce((obj, [, value]) => [...obj, value], []);
        if (ids.length > 0) {
            targetNode = node;
        }
    });
    return targetNode;
};

const findBudgetLineRow = (gridApi: GridApi, budgetLineId: number) => {
    const blIdPattern = new RegExp(
        `${`${BUDGET_LINE_ID}${SPLIT_BY}`.replace('.', '\\.')}[1-9][0-9]{3}`,
    );
    return findInGrid(
        gridApi,
        ([key, value]) => blIdPattern.test(key) && value === budgetLineId,
    );
};

const fullyExpandRow = (node: IRowNode) => {
    let iterator: IRowNode | null = node;
    while (iterator?.isExpandable()) {
        iterator.setExpanded(true);
        iterator = iterator?.parent;
    }
};

const moveToPage = (gridApi: GridApi, node: IRowNode, rowPerPage: number) =>
    gridApi.paginationGoToPage(Math.floor((node.rowIndex || 0) / rowPerPage));

// This hook is responsible for :
// - finding the budget_line row that contains the inscription from InscriptionIdParam
// - expanding this row and all its parents (if grid is grouped)
// - going to the right page so that this row is visible according to RowPerPage argument
export const useFindAndOpenInscriptionGrid = (
    inscriptions: InscriptionT[],
    isLoading: boolean,
    gridApi: GridApi | null,
    inscriptionIdParam: string | null,
    rowPerPage: number,
) => {
    const [lastSelectedInscription, setLastSelectedInscription] =
        useLocalStorage<string | null>('lastSelectedInscription');
    if (inscriptionIdParam && lastSelectedInscription)
        setLastSelectedInscription(null);
    useEffect(() => {
        if (
            isLoading ||
            !gridApi ||
            !rowPerPage ||
            rowPerPage === DEFAULT_ROW_PER_PAGE ||
            !(inscriptionIdParam || lastSelectedInscription)
        ) {
            return;
        }
        const budgetLineId = inscriptions.find(
            (e) =>
                e[INSCRIPTION_ID] ===
                parseInt((inscriptionIdParam || lastSelectedInscription)!, 10),
        )?.[BUDGET_LINE_ID];
        if (!budgetLineId) return;
        const targetNode = findBudgetLineRow(gridApi, budgetLineId);
        if (!targetNode) {
            return;
        }
        fullyExpandRow(targetNode);
        moveToPage(gridApi, targetNode, rowPerPage);
        setLastSelectedInscription(null);
    }, [
        isLoading,
        gridApi,
        inscriptionIdParam,
        rowPerPage,
        lastSelectedInscription,
    ]);
};
